import { apiRequest, readJsonOrThrow } from "@/api/client";
import * as clientParamBuilder from "meta-capi-param-builder-clientjs";
import { readAffiliateReferralCode } from "@/utils/affiliateAttribution";

const SIGNUP_ATTRIBUTION_STORAGE_KEY = "clipr_signup_attribution";
export const META_PIXEL_ID = "1723992865600908";

function getCurrentUrl() {
  return window.location.href;
}

export function captureSignupAttribution() {
  clientParamBuilder.processAndCollectParams(getCurrentUrl());
  return persistSignupAttribution();
}

export function buildSignupAttribution() {
  clientParamBuilder.processAndCollectParams(getCurrentUrl());
  return {
    meta_fbp: clientParamBuilder.getFbp(),
    meta_fbc: clientParamBuilder.getFbc(),
    meta_event_source_url: getCurrentUrl(),
  };
}

const ATTRIBUTION_MARKERS_RE = /[?&](utm_[a-z]+|fbclid)=/i;

function hasAttributionMarkers(url) {
  return ATTRIBUTION_MARKERS_RE.test(String(url || ""));
}

// Returns attribution preferring the current URL when it still carries
// utm_*/fbclid params (fresh ad click); otherwise falls back to the
// attribution persisted at landing time. Prevents losing UTMs when the
// user reloads or the URL is cleaned before submitting the try funnel.
// Unlike persistSignupAttribution(), it never overwrites a stored
// attribution with a URL that has no attribution markers.
export function getPersistedSignupAttribution() {
  const current = buildSignupAttribution();
  const referralCode = readAffiliateReferralCode();
  if (hasAttributionMarkers(current.meta_event_source_url)) {
    try {
      localStorage.setItem(SIGNUP_ATTRIBUTION_STORAGE_KEY, JSON.stringify(current));
    } catch (error) {
      // Storage unavailable (private mode / in-app browser): still use fresh attribution.
    }
    return { ...current, referral_code: referralCode || undefined };
  }
  let stored = null;
  try {
    const raw = localStorage.getItem(SIGNUP_ATTRIBUTION_STORAGE_KEY);
    stored = raw ? JSON.parse(raw) : null;
  } catch (error) {
    stored = null;
  }
  if (stored && hasAttributionMarkers(stored.meta_event_source_url)) {
    return {
      meta_fbp: current.meta_fbp || stored.meta_fbp || "",
      meta_fbc: current.meta_fbc || stored.meta_fbc || "",
      meta_event_source_url: stored.meta_event_source_url,
      referral_code: referralCode || undefined,
    };
  }
  return { ...current, referral_code: referralCode || undefined };
}

export function splitMetaName(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

export function buildMetaAdvancedMatching({ email, fullName, externalId, phone } = {}) {
  const { firstName, lastName } = splitMetaName(fullName);
  const userData = {};
  if (email) userData.em = String(email).trim().toLowerCase();
  if (firstName) userData.fn = firstName.toLowerCase();
  if (lastName) userData.ln = lastName.toLowerCase();
  if (externalId) userData.external_id = String(externalId).trim();
  if (phone) userData.ph = String(phone).replace(/\D/g, "");
  return userData;
}

export function persistSignupAttribution() {
  const attribution = buildSignupAttribution();
  localStorage.setItem(SIGNUP_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  return attribution;
}

function readSignupAttribution() {
  const raw = localStorage.getItem(SIGNUP_ATTRIBUTION_STORAGE_KEY);
  return raw ? JSON.parse(raw) : buildSignupAttribution();
}

function buildSignupAttributionHeaders() {
  const attribution = readSignupAttribution();
  return {
    "X-Clipr-Signup-Source-Url": attribution.meta_event_source_url || "",
    "X-Clipr-Meta-Fbp": attribution.meta_fbp || "",
    "X-Clipr-Meta-Fbc": attribution.meta_fbc || "",
    "X-Clipr-Referral-Code": readAffiliateReferralCode(),
  };
}

export async function fetchCurrentUserBootstrap(session) {
  const response = await apiRequest("/momo/bootstrap", {
    session,
    headers: buildSignupAttributionHeaders(),
    includeContentType: false,
  });
  if (response.status === 401) {
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }
  return readJsonOrThrow(response, "Failed to bootstrap current user");
}

export async function fetchCurrentUserProfile(session) {
  const response = await apiRequest("/momo/me", {
    session,
    includeContentType: false,
  });
  return readJsonOrThrow(response, "Failed to fetch current user");
}

export async function ensureCurrentUserInfo(session) {
  const response = await apiRequest("/momo/get-users/ensure-user-info", {
    method: "POST",
    session,
    headers: buildSignupAttributionHeaders(),
    body: {},
  });
  return readJsonOrThrow(response, "Failed to ensure current user");
}
